// OVO-120 — Tours tab on PartnerDetail.
//
// Three stacked sections:
//   1. Strategy radio — picks which lookup runs at order-create time.
//   2. Tour catalog — partner-scoped Tour list with add/edit/delete.
//   3. Mapping rules editor — strategy-dependent.
//        ZIP_CODE    → flat table (zip + day + tour), one row per rule.
//        EXT_TOUR_ID → flat table (extTourIdPattern + tour).
//        DAY_OF_WEEK → flat table (day + tour).
//
// v1 ships ZIP_CODE first (BEAN unblock) but the model + editor handles
// all three strategies. Backend already accepts/validates each shape.
//
// Bulk CSV paste import is OUT of scope for v1 — operator can add rows
// manually; a follow-up will paste the Airtable export in one shot.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Tooltip,
  Table,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { listSitesForPartner } from '../modules/sites/api';

import { updatePartner } from '../modules/partners/api';
import {
  listToursForPartner,
  createTour,
  updateTour,
  deleteTour,
  listMappingRulesForPartner,
  createMappingRule,
  updateMappingRule,
  deleteMappingRule,
} from '../modules/tours/api';

// Mon..Sun. ISO 8601 — matches the backend (DateTime.isoWeekday in Luxon).
const DAY_OPTIONS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

// The strategy options + a small description blurb per choice so the
// operator knows which one fits their partner.
const STRATEGY_OPTIONS = [
  {
    value: 'NONE',
    label: 'None — no auto-mapping',
    description: 'Orders are created without an auto-resolved tour (current behavior).',
  },
  {
    value: 'ZIP_CODE',
    label: 'By delivery zip code + day of week',
    description: 'Use case: BEAN. Extracts the zip from the delivery address; matches (zip, day) to a tour.',
  },
  {
    value: 'EXT_TOUR_ID',
    label: 'By external tour ID',
    description: 'Use case: ET. Partner sends an extTourId field on the order import; that exact value maps to a tour.',
  },
  {
    value: 'DAY_OF_WEEK',
    label: 'By day of week only',
    description: 'Use case: EPI, STER. One tour per day. Each order brings its own pickup (tour pickup left blank).',
  },
];

export default function PartnerTours({ partner, onPartnerUpdated }) {
  const [strategy, setStrategy] = useState(partner?.tourMappingStrategy || 'NONE');
  const [savingStrategy, setSavingStrategy] = useState(false);

  const [tours, setTours] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tourModalOpen, setTourModalOpen] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [tourForm] = Form.useForm();

  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm] = Form.useForm();

  // ─── Loaders ──────────────────────────────────────────────────────────

  const reloadTours = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    try {
      const { response } = await listToursForPartner(partner.id);
      setTours(response || []);
    } catch (e) {
      message.error(e?.message || 'Failed to load tours');
    } finally {
      setLoading(false);
    }
  }, [partner?.id]);

  const reloadRules = useCallback(async () => {
    if (!partner?.id) return;
    try {
      const { response } = await listMappingRulesForPartner(partner.id);
      setRules(response || []);
    } catch (e) {
      message.error(e?.message || 'Failed to load mapping rules');
    }
  }, [partner?.id]);

  useEffect(() => {
    reloadTours();
    reloadRules();
  }, [reloadTours, reloadRules]);

  useEffect(() => {
    setStrategy(partner?.tourMappingStrategy || 'NONE');
  }, [partner?.tourMappingStrategy]);

  // ─── Strategy save ────────────────────────────────────────────────────

  const handleStrategyChange = async (e) => {
    const newStrategy = e.target.value;
    setSavingStrategy(true);
    try {
      const { response } = await updatePartner({
        id: partner.id,
        tourMappingStrategy: newStrategy,
      });
      setStrategy(newStrategy);
      message.success('Mapping strategy updated');
      onPartnerUpdated?.(response);
    } catch (err) {
      message.error(err?.message || 'Failed to update strategy');
    } finally {
      setSavingStrategy(false);
    }
  };

  // ─── Tour modal ───────────────────────────────────────────────────────

  const openTourModal = (tour) => {
    setEditingTour(tour);
    if (tour) {
      tourForm.setFieldsValue({
        ...tour,
        defaultTypes: (tour.defaultTypes || []).join(', '),
      });
    } else {
      tourForm.resetFields();
      tourForm.setFieldsValue({ isActive: true });
    }
    setTourModalOpen(true);
  };

  // Auto-retour — partner sites for the retour picker (loaded once per partner).
  const [retourSites, setRetourSites] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!partner?.id) return undefined;
    listSitesForPartner(partner.id)
      .then(({ response }) => {
        const list = response?.sites || response?.items || (Array.isArray(response) ? response : []);
        if (alive) setRetourSites(list);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [partner?.id]);

  const retourSiteOptions = useMemo(
    () =>
      (retourSites || []).map((s) => ({
        value: s.id,
        label: `${s.name} — ${s.address}`,
      })),
    [retourSites],
  );

  const handleRetourSitePick = (siteId) => {
    const site = (retourSites || []).find((s) => s.id === siteId);
    if (!site) {
      tourForm.setFieldsValue({ retourSiteId: null });
      return;
    }
    tourForm.setFieldsValue({
      retourSiteId: site.id,
      retourAddress: site.address,
      retourLat: site.lat ?? undefined,
      retourLng: site.lng ?? undefined,
    });
  };

  // #138 — renaming a tour code is not cosmetic: the code is sent to the
  // partner in every webhook (Bean's carrier_tracking_ref = <tourRef>/<NN>),
  // and the orders already created + the shift skills keep the old code. The
  // Sep 22 2026 rename of BEAN's Tuesday tours broke Bean2Me's Tuesdays for
  // two weeks. Ask before renaming.
  const confirmTourRename = (oldRef, newRef) =>
    new Promise((resolve) => {
      Modal.confirm({
        title: `Rename tour "${oldRef}" to "${newRef}"?`,
        width: 560,
        content: (
          <div>
            {partner?.webhookEnabled && (
              <p>
                <strong>{partner?.name}</strong> receives this code in its webhooks (e.g. Bean&apos;s tracking ref
                &quot;&lt;tour&gt;/01&quot;). Their system may not recognise a new code — check with them first.
              </p>
            )}
            <p>
              Orders already created keep &quot;{oldRef}&quot; as their tour and type, and shifts whose skills list
              &quot;{oldRef}&quot; won&apos;t match new orders (they stay unscheduled at Optimize) until you update
              those skills.
            </p>
            <p>For a one-off change (holiday), create a separate tour instead of renaming this one.</p>
          </div>
        ),
        okText: 'Rename anyway',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleSaveTour = async () => {
    try {
      const values = await tourForm.validateFields();
      // Normalize defaultTypes from comma-separated input back to string[].
      const defaultTypes = (values.defaultTypes || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = { ...values, defaultTypes };
      if (
        editingTour &&
        values.tourRef?.trim() !== editingTour.tourRef &&
        !(await confirmTourRename(editingTour.tourRef, values.tourRef?.trim()))
      ) {
        return;
      }
      if (editingTour) {
        await updateTour(editingTour.id, payload);
        message.success('Tour updated');
      } else {
        await createTour(partner.id, payload);
        message.success('Tour created');
      }
      setTourModalOpen(false);
      setEditingTour(null);
      tourForm.resetFields();
      reloadTours();
    } catch (e) {
      if (e?.errorFields) return; // validation, AntD already surfaced
      message.error(e?.message || 'Failed to save tour');
    }
  };

  const handleDeleteTour = async (id) => {
    try {
      await deleteTour(id);
      message.success('Tour deleted');
      reloadTours();
      reloadRules(); // CASCADE removed related rules; refresh
    } catch (e) {
      message.error(e?.message || 'Failed to delete tour');
    }
  };

  // ─── Rule modal ───────────────────────────────────────────────────────

  const openRuleModal = (rule) => {
    setEditingRule(rule);
    if (rule) {
      ruleForm.setFieldsValue(rule);
    } else {
      ruleForm.resetFields();
    }
    setRuleModalOpen(true);
  };

  const handleSaveRule = async () => {
    try {
      const values = await ruleForm.validateFields();
      // Service-layer enforces the strategy-specific shape; we just send
      // every field the form has and let the backend reject bad combos.
      const payload = {
        tourId: values.tourId,
        zipCode: values.zipCode || undefined,
        extTourIdPattern: values.extTourIdPattern || undefined,
        dayOfWeek: values.dayOfWeek ?? undefined,
      };
      if (editingRule) {
        await updateMappingRule(editingRule.id, payload);
        message.success('Mapping rule updated');
      } else {
        await createMappingRule(partner.id, payload);
        message.success('Mapping rule created');
      }
      setRuleModalOpen(false);
      setEditingRule(null);
      ruleForm.resetFields();
      reloadRules();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Failed to save rule');
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      await deleteMappingRule(id);
      message.success('Rule deleted');
      reloadRules();
    } catch (e) {
      message.error(e?.message || 'Failed to delete rule');
    }
  };

  // ─── Computed bits ────────────────────────────────────────────────────

  const activeTourOptions = useMemo(
    () => tours.filter((t) => t.isActive).map((t) => ({ value: t.id, label: t.tourRef })),
    [tours],
  );

  const tourLookup = useMemo(() => {
    const m = new Map();
    tours.forEach((t) => m.set(t.id, t));
    return m;
  }, [tours]);

  // ─── Tour catalog columns ─────────────────────────────────────────────

  const tourColumns = [
    {
      title: 'Tour ref',
      dataIndex: 'tourRef',
      key: 'tourRef',
      render: (v) => <strong>{v}</strong>,
    },
    {
      title: 'Pickup',
      key: 'pickup',
      render: (_, t) =>
        t.pickupName || t.pickupAddress ? (
          <div style={{ fontSize: 12 }}>
            {t.pickupName && <div>{t.pickupName}</div>}
            {t.pickupAddress && <div style={{ color: 'var(--text-muted)' }}>{t.pickupAddress}</div>}
          </div>
        ) : (
          <span style={{ color: '#bbb', fontStyle: 'italic' }}>— multi-pickup tour</span>
        ),
    },
    {
      title: 'Window',
      key: 'window',
      width: 110,
      render: (_, t) => (t.pickupTimeStart && t.pickupTimeEnd ? `${t.pickupTimeStart}–${t.pickupTimeEnd}` : '—'),
    },
    {
      title: 'Retour',
      key: 'retour',
      width: 90,
      render: (_, t) =>
        t.retourEnabled ? (
          <Tooltip title={t.retourAddress || ''}>
            <Tag color="green">♻️ Retour</Tag>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: 'Default types',
      dataIndex: 'defaultTypes',
      key: 'defaultTypes',
      render: (arr) =>
        arr?.length > 0 ? (
          <Space size={[0, 4]} wrap>
            {arr.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: 'Rules',
      key: 'rules',
      width: 60,
      render: (_, t) => t._count?.mappingRules ?? 0,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag>Archived</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, t) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openTourModal(t)} />
          <Popconfirm
            title="Delete this tour?"
            description="Mapping rules pointing at this tour will also be removed."
            onConfirm={() => handleDeleteTour(t.id)}
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // OVO-292 — ZIP_CODE without a '*' catch-all is a silent trap: an order
  // for an UNLISTED zip is a tour-mapping miss → no pickup → REFUSED at
  // upload (the BUT 1201 incident: the GE-1/GE-2 split had explicit lists
  // only). Days that have rules but no wildcard get a warning here.
  const zipDaysWithoutCatchAll = useMemo(() => {
    if (strategy !== 'ZIP_CODE') return [];
    const byDay = new Map();
    for (const r of rules) {
      if (r.dayOfWeek == null) continue;
      const cur = byDay.get(r.dayOfWeek) || { rules: 0, wildcard: false };
      cur.rules += 1;
      if (String(r.zipCode || '').trim() === '*') cur.wildcard = true;
      byDay.set(r.dayOfWeek, cur);
    }
    return DAY_OPTIONS.filter((d) => byDay.has(d.value) && !byDay.get(d.value).wildcard).map((d) => d.label);
  }, [strategy, rules]);

  // Rules left on an archived tour (BEAN's "…-Férié" holiday tours, Sep 2026)
  // are ignored by the resolver — surfaced so ops can clean them up.
  const rulesOnArchivedTours = useMemo(
    () => rules.filter((r) => tourLookup.get(r.tourId)?.isActive === false).length,
    [rules, tourLookup],
  );

  // ─── Rules columns — built per strategy so the operator only sees the
  // columns relevant to the current lookup ────────────────────────────

  const rulesColumns = useMemo(() => {
    const cols = [
      {
        title: 'Tour',
        dataIndex: 'tourId',
        key: 'tour',
        render: (id) => {
          const t = tourLookup.get(id);
          if (!t) return <span style={{ color: '#c00' }}>(missing tour {id})</span>;
          return (
            <Space size={6} wrap>
              <strong>{t.tourRef}</strong>
              {!t.isActive && (
                <Tooltip title="The tour is archived: this rule is ignored when orders are created. Delete it, or reactivate the tour.">
                  <Tag color="orange">Archived tour — ignored</Tag>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ];
    if (strategy === 'ZIP_CODE' || strategy === 'DAY_OF_WEEK') {
      cols.push({
        title: 'Day',
        dataIndex: 'dayOfWeek',
        key: 'dayOfWeek',
        width: 80,
        render: (d) => DAY_OPTIONS.find((o) => o.value === d)?.label || '—',
      });
    }
    if (strategy === 'ZIP_CODE') {
      cols.push({
        title: 'Zip code',
        dataIndex: 'zipCode',
        key: 'zipCode',
        width: 100,
      });
    }
    if (strategy === 'EXT_TOUR_ID') {
      cols.push({
        title: 'External tour ID',
        dataIndex: 'extTourIdPattern',
        key: 'extTourIdPattern',
      });
    }
    cols.push({
      title: '',
      key: 'actions',
      width: 110,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openRuleModal(r)} />
          <Popconfirm
            title="Delete this rule?"
            onConfirm={() => handleDeleteRule(r.id)}
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    });
    return cols;
  }, [strategy, tourLookup]);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="partner-tours">
      {/* Section 1 — strategy */}
      <Card title="Tour mapping strategy" size="small" style={{ marginBottom: 16 }} loading={savingStrategy}>
        <Radio.Group onChange={handleStrategyChange} value={strategy}>
          <Space direction="vertical">
            {STRATEGY_OPTIONS.map((opt) => (
              <Radio key={opt.value} value={opt.value}>
                <strong>{opt.label}</strong>
                <div style={{ color: 'var(--text-secondary)', fontSize: 12, paddingLeft: 24 }}>{opt.description}</div>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Card>

      {/* Section 2 — tour catalog */}
      <Card
        title="Tour catalog"
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openTourModal(null)}>
            Add tour
          </Button>
        }
      >
        <Table
          dataSource={tours}
          columns={tourColumns}
          rowKey="id"
          size="small"
          pagination={tours.length > 20 ? { pageSize: 20 } : false}
          loading={loading}
          locale={{
            emptyText:
              'No tours yet. Add the partner\'s recurring tours (e.g. "BEAN-Lundi-Bike-GVA-01") here first, then add mapping rules below.',
          }}
        />
      </Card>

      {/* Section 3 — mapping rules */}
      {strategy === 'NONE' ? (
        <Alert
          type="info"
          showIcon
          message="No mapping rules"
          description="Pick a strategy above to enable per-order tour resolution."
        />
      ) : (
        <Card
          title="Mapping rules"
          size="small"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openRuleModal(null)}
              disabled={activeTourOptions.length === 0}
            >
              Add rule
            </Button>
          }
        >
          {activeTourOptions.length === 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Add an active tour first"
              description="Mapping rules point at a tour — create at least one active tour in the catalog above before adding rules."
            />
          )}
          {zipDaysWithoutCatchAll.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              data-testid="zip-catchall-warning"
              message={`No '*' catch-all rule on ${zipDaysWithoutCatchAll.join(', ')}`}
              description="On those days an order for a zip that is in NO list is a tour-mapping miss: no pickup gets filled and the upload REFUSES the order (it then only exists in Airtable / the API-intake ledger). Add a rule with zip code * pointing at the tour that should absorb unlisted zips — explicit lists always win over the wildcard."
            />
          )}
          {rulesOnArchivedTours > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              data-testid="rules-on-archived-tours-warning"
              message={`${rulesOnArchivedTours} rule${rulesOnArchivedTours > 1 ? 's point' : ' points'} at an archived tour`}
              description="These rules are ignored when orders are created: the zips fall to the active tours' rules (or the '*' catch-all). Delete them if the tour is retired for good."
            />
          )}
          <Table
            dataSource={rules}
            columns={rulesColumns}
            rowKey="id"
            size="small"
            pagination={rules.length > 50 ? { pageSize: 50 } : false}
            locale={{ emptyText: 'No mapping rules yet.' }}
          />
        </Card>
      )}

      {/* Tour add/edit modal */}
      <Modal
        title={editingTour ? `Edit tour ${editingTour.tourRef}` : 'Add tour'}
        open={tourModalOpen}
        onCancel={() => {
          setTourModalOpen(false);
          setEditingTour(null);
        }}
        onOk={handleSaveTour}
        okText={editingTour ? 'Save' : 'Create'}
        width={620}
      >
        <Form form={tourForm} layout="vertical">
          <Form.Item
            name="tourRef"
            label="Tour ref"
            rules={[{ required: true, message: 'Required' }]}
            extra={
              editingTour
                ? 'Renaming changes the code the partner receives and no longer matches existing orders / shift skills.'
                : 'e.g. "BEAN-Lundi-Bike-GVA-01"'
            }
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="defaultTypes"
            label="Default types"
            extra="Comma-separated. Applied to orders that don't bring their own types."
          >
            <Input placeholder="e.g. EPI-Lundi-Bike-GVA-V" />
          </Form.Item>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Pickup info — leave blank for multi-pickup tours (EPI/STER). When set, the tour overrides the order's pickup
            at create time.
          </div>
          <Form.Item name="pickupName" label="Pickup name">
            <Input />
          </Form.Item>
          <Form.Item name="pickupAddress" label="Pickup address">
            <Input />
          </Form.Item>
          <Space style={{ display: 'flex' }} size="small">
            <Form.Item name="pickupLat" label="Pickup lat" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} step="0.000001" />
            </Form.Item>
            <Form.Item name="pickupLng" label="Pickup lng" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} step="0.000001" />
            </Form.Item>
          </Space>
          <Form.Item name="pickupPhone" label="Pickup phone">
            <Input />
          </Form.Item>
          <Space style={{ display: 'flex' }} size="small">
            <Form.Item name="pickupTimeStart" label="Pickup window start" style={{ flex: 1 }}>
              <Input placeholder="07:00" />
            </Form.Item>
            <Form.Item name="pickupTimeEnd" label="Pickup window end" style={{ flex: 1 }}>
              <Input placeholder="09:00" />
            </Form.Item>
            <Form.Item name="pickupDuration" label="Pickup duration (min)" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Space>
          <Form.Item
            name="pickupNote"
            label="Pickup note (instructions tournée)"
            extra="Static pickup instructions — e.g. door code, where to park. Always copied onto the order when set."
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Delivery defaults — used to fill the order's delivery window / duration / preparation if the order doesn't
            bring its own (Airtable "Destination from/to", "Dropoff task duration", "Preparation").
          </div>
          <Space style={{ display: 'flex' }} size="small">
            <Form.Item name="defaultDeliveryTimeStart" label="Delivery window start" style={{ flex: 1 }}>
              <Input placeholder="10:00" />
            </Form.Item>
            <Form.Item name="defaultDeliveryTimeEnd" label="Delivery window end" style={{ flex: 1 }}>
              <Input placeholder="13:00" />
            </Form.Item>
            <Form.Item name="defaultDeliveryDuration" label="Delivery duration (min)" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Space>
          <Form.Item name="defaultPreparationMode" label="Default preparation mode">
            <Input placeholder="Normal" />
          </Form.Item>

          {/* Auto-retour (Aug 2026, direct-send prerequisite) — the Airtable
              automation that created the "Retour" orders is bypassed by
              direct-send, so the planner owns them via this per-tour config. */}
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, marginTop: 8 }}>
            <b>Retour — empties drop-off</b>
          </div>
          <Form.Item
            name="retourEnabled"
            valuePropName="checked"
            label={
              <span>
                Auto-create retour order (empties drop-off)
                <Tooltip
                  overlayStyle={{ maxWidth: 420 }}
                  title={
                    "Every day this tour has at least one order, the planner automatically creates a SERVICE order telling the driver to deposit the empties collected during the tour at this address before closing the tour — the \"Retour\" stop that used to come from the Airtable automation. It is created together with the day's FIRST order and inherits the tour's types (skills). Deleting a day's retour order keeps it deleted for that day."
                  }
                >
                  <InfoCircleOutlined style={{ marginLeft: 6, color: '#9e9e9e' }} />
                </Tooltip>
              </span>
            }
          >
            <Switch />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.retourEnabled !== cur.retourEnabled}>
            {({ getFieldValue }) =>
              getFieldValue('retourEnabled') ? (
                <>
                  <Form.Item label="Retour site (pre-fills the address)">
                    <Select
                      allowClear
                      showSearch
                      placeholder={retourSiteOptions.length ? 'Pick a partner site' : 'No sites for this partner'}
                      options={retourSiteOptions}
                      optionFilterProp="label"
                      value={tourForm.getFieldValue('retourSiteId')}
                      onChange={handleRetourSitePick}
                    />
                  </Form.Item>
                  <Form.Item name="retourSiteId" hidden>
                    <InputNumber />
                  </Form.Item>
                  <Form.Item
                    name="retourName"
                    label="Retour name"
                    extra='Shown to the driver — defaults to "<CODE>, Retour -<tourRef>"'
                  >
                    <Input placeholder="ET, Retour -ET-Mercredi-Van-GVA-A-U" />
                  </Form.Item>
                  <Form.Item
                    name="retourAddress"
                    label="Retour address"
                    rules={[{ required: true, message: 'Required when retour is enabled' }]}
                  >
                    <Input />
                  </Form.Item>
                  <Space style={{ display: 'flex' }} size="small">
                    <Form.Item
                      name="retourLat"
                      label="Retour lat"
                      style={{ flex: 1 }}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber style={{ width: '100%' }} step="0.000001" />
                    </Form.Item>
                    <Form.Item
                      name="retourLng"
                      label="Retour lng"
                      style={{ flex: 1 }}
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <InputNumber style={{ width: '100%' }} step="0.000001" />
                    </Form.Item>
                  </Space>
                  <Space style={{ display: 'flex' }} size="small">
                    <Form.Item
                      name="retourTimeStart"
                      label="Window start"
                      style={{ flex: 1 }}
                      extra="Blank = tour's delivery window end"
                    >
                      <Input placeholder="15:00" />
                    </Form.Item>
                    <Form.Item name="retourTimeEnd" label="Window end" style={{ flex: 1 }}>
                      <Input placeholder="18:00" />
                    </Form.Item>
                    <Form.Item name="retourDuration" label="Duration (min)" style={{ flex: 1 }} extra="Blank = 10">
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Space>
                  <Form.Item
                    name="retourNote"
                    label="Retour note (driver instructions)"
                    extra='Shown to the driver on the retour stop — e.g. "Déposer tous les bacs vides et prendre une photo."'
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Rule add/edit modal */}
      <Modal
        title={editingRule ? 'Edit mapping rule' : 'Add mapping rule'}
        open={ruleModalOpen}
        onCancel={() => {
          setRuleModalOpen(false);
          setEditingRule(null);
        }}
        onOk={handleSaveRule}
        okText={editingRule ? 'Save' : 'Create'}
      >
        <Form form={ruleForm} layout="vertical">
          <Form.Item name="tourId" label="Tour" rules={[{ required: true, message: 'Required' }]}>
            <Select
              options={activeTourOptions}
              placeholder="Pick a tour from the catalog"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          {strategy === 'ZIP_CODE' && (
            <>
              <Form.Item
                name="zipCode"
                label="Zip code"
                rules={[{ required: true, message: 'Required for ZIP_CODE strategy' }]}
                extra="A comma-separated list (1205, 1206, 1207) or * as the day's catch-all for every unlisted zip (explicit lists win)."
              >
                <Input placeholder="1208 — or 1205, 1206 — or *" />
              </Form.Item>
              <Form.Item
                name="dayOfWeek"
                label="Day of week"
                rules={[{ required: true, message: 'Required for ZIP_CODE strategy' }]}
              >
                <Select options={DAY_OPTIONS} placeholder="Pick a day" />
              </Form.Item>
            </>
          )}
          {strategy === 'EXT_TOUR_ID' && (
            <Form.Item
              name="extTourIdPattern"
              label="External tour ID"
              rules={[{ required: true, message: 'Required for EXT_TOUR_ID strategy' }]}
              extra="Exact match (no regex). E.g. the value the partner sends as `tourCode` on the order import."
            >
              <Input placeholder="GVA-CRG" />
            </Form.Item>
          )}
          {strategy === 'DAY_OF_WEEK' && (
            <Form.Item
              name="dayOfWeek"
              label="Day of week"
              rules={[{ required: true, message: 'Required for DAY_OF_WEEK strategy' }]}
            >
              <Select options={DAY_OPTIONS} placeholder="Pick a day" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
